/**
 * Auth-wide password-pair validation, shared by the reset-password and
 * onboarding features (both set a new password from a one-time token). Pure
 * domain helper (no React, no Redux, no fetch) and it depends on NO sibling
 * feature (hermeticity: `_shared` must stay import-free of the features).
 */
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '@shared/constants/password';

/** Re-exported so consumers keep importing the contract from their own domain layer. */
export { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH };

/** The editable pair every consumer's draft carries. */
export interface PasswordPair {
  password: string;
  confirmPassword: string;
}

/** Field-level validation errors, keyed by pair field name. Empty = valid. */
export type PasswordPairErrors = Partial<Record<keyof PasswordPair, string>>;

/** UTF-8 byte length; bcrypt's real boundary, mirrored from the backend policy. */
const byteLength = (s: string): number => new TextEncoder().encode(s).length;

/**
 * Validate a password pair. Mirrors the backend contract
 * (`backend/src/features/auth/domain/passwordPolicy.ts`), which reads its bounds
 * from the same `@shared/constants/password`. The length rules report before the
 * mismatch, so a too-short password never also flags the confirmation.
 */
export function validatePasswordPair(pair: PasswordPair): PasswordPairErrors {
  const errors: PasswordPairErrors = {};
  if (pair.password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  } else if (byteLength(pair.password) > MAX_PASSWORD_LENGTH) {
    errors.password = `Password must be at most ${MAX_PASSWORD_LENGTH} characters.`;
  } else if (pair.password !== pair.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }
  return errors;
}
